import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { SERVICES, PROCESS } from "@/data/content";
import { scrollToId } from "@/lib/smoothScroll";

const reveal = {
  hidden: { opacity: 0, y: 36 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Services = () => {
  const [openStep, setOpenStep] = useState(0);

  return (
    <section id="services" className="py-24 sm:py-32 lg:py-40" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
        <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={0} className="font-mono text-xs uppercase tracking-[0.24em] text-amber mb-6">
          Services &amp; editorial offerings
        </motion.p>
        <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={1} className="font-serif text-2xl sm:text-3xl lg:text-4xl tracking-tight leading-[1.15] max-w-2xl">
          Every deliverable, from hero line to error state, <em className="text-wine">engineered to be believed.</em>
        </motion.h2>

        <div className="mt-16 lg:mt-24 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-7">
            {SERVICES.map((s, i) => (
              <motion.button
                key={s.title}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                custom={i}
                onClick={() => scrollToId("contact")}
                className="group w-full text-left grid sm:grid-cols-[64px_1fr_32px] gap-4 sm:gap-6 items-start py-8 border-t border-line last:border-b hover:bg-cream/70 transition-colors duration-300 px-2 sm:px-4"
                data-testid={`service-item-${i}`}
              >
                <span className="font-mono text-xs text-subtle pt-1.5">/{String(i + 1).padStart(2, "0")}</span>
                <span>
                  <span className="block font-serif text-xl sm:text-2xl tracking-tight group-hover:text-wine transition-colors duration-300">
                    {s.title}
                  </span>
                  <span className="block mt-2 text-sm leading-relaxed text-subtle max-w-lg">{s.desc}</span>
                  <span className="mt-4 flex flex-wrap gap-2">
                    {s.tags.map((t) => (
                      <span key={t} className="px-3 py-1 rounded-full border border-ink/15 font-mono text-[10px] uppercase tracking-[0.14em] text-subtle">
                        {t}
                      </span>
                    ))}
                  </span>
                </span>
                <ArrowUpRight size={20} className="mt-1 text-subtle transition-all duration-300 group-hover:text-wine group-hover:translate-x-1 group-hover:-translate-y-1" />
              </motion.button>
            ))}
          </div>

          <div className="lg:col-span-5">
            <motion.div
              variants={reveal}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-60px" }}
              custom={2}
              className="lg:sticky lg:top-28 bg-ink text-paper p-8 sm:p-10"
              data-testid="process-panel"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber mb-3">The working method</p>
              <h3 className="font-serif text-2xl tracking-tight mb-2">Faster ideation, human ownership.</h3>
              <p className="text-sm leading-relaxed text-paper/60 mb-8">
                AI assists the exploration. A human writes, checks, and stands behind every shipped word.
              </p>
              <div className="flex flex-col">
                {PROCESS.map((p, i) => (
                  <div key={p.step} className="border-t border-paper/15 last:border-b">
                    <button
                      onClick={() => setOpenStep(openStep === i ? -1 : i)}
                      className="w-full flex items-center gap-4 py-4 text-left"
                      data-testid={`process-step-${i}`}
                    >
                      <span className="font-mono text-xs text-amber">{p.step}</span>
                      <span className="flex-1 font-serif text-lg tracking-tight">{p.title}</span>
                      <ChevronDown
                        size={16}
                        className={`text-paper/50 transition-transform duration-300 ${openStep === i ? "rotate-180" : ""}`}
                      />
                    </button>
                    <AnimatePresence initial={false}>
                      {openStep === i && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden"
                        >
                          <p className="pb-5 pl-9 text-sm leading-relaxed text-paper/70">{p.desc}</p>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
